import type { IComponent } from "app/@types/ISwSettings";
import { isHidden, isLocked, isUnavailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import type { SidebarAccessMap } from "app/components/React/QuickSearch/pageData/getSidebarAccessMap";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

type ComponentAccess = "open" | "hook" | "hidden" | "unknown";

export type AccessVerdict = { kind: "drop" } | { kind: "open" } | { kind: "lock"; hookKey: string };

type LockVerdict = Extract<AccessVerdict, { kind: "lock" }>;

const DROP: AccessVerdict = { kind: "drop" };
const OPEN: AccessVerdict = { kind: "open" };

const lock = (hookKey: string): LockVerdict => ({ kind: "lock", hookKey });

const isLockVerdict = (verdict: AccessVerdict): verdict is LockVerdict => verdict.kind === "lock";

const MAX_PARENT_WALK_DEPTH = 20;

const safeGetState = (uiRouterState: string): IRouterState | null => {
    try {
        return proNavigator.getState(uiRouterState) ?? null;
    } catch {
        return null;
    }
};

const safeResolveConfigId = (state: IRouterState | null | undefined): string | null => {
    if (!state) {
        return null;
    }

    return proNavigator.getConfigId(state);
};

const isRouterStateHidden = (state: IRouterState): boolean => {
    if (typeof state.isHidden === "function") {
        return state.isHidden();
    }

    return state.isHidden === true;
};

const componentAccess = (component: IComponent | undefined): ComponentAccess => {
    if (!component || isUnavailable(component)) {
        return "unknown";
    }

    if (isHidden(component)) {
        return "hidden";
    }

    if (isLocked(component)) {
        return "hook";
    }

    return "open";
};

const verdictForConfigId = (configId: string | null): AccessVerdict => {
    if (!configId) {
        return OPEN;
    }

    switch (componentAccess(swSettings?.components?.[configId])) {
        case "hidden":
            return DROP;
        case "hook":
            return lock(configId);
        default:
            return OPEN;
    }
};

const verdictForState = (state: IRouterState): AccessVerdict => {
    if (isRouterStateHidden(state)) {
        return DROP;
    }

    return verdictForConfigId(safeResolveConfigId(state));
};

const collectAncestors = (state: IRouterState): IRouterState[] => {
    const ancestors: IRouterState[] = [];
    let cursor = state.parent ? safeGetState(state.parent) : null;

    while (cursor && ancestors.length < MAX_PARENT_WALK_DEPTH) {
        ancestors.push(cursor);
        cursor = cursor.parent ? safeGetState(cursor.parent) : null;
    }

    return ancestors;
};

const verdictFromAncestors = (state: IRouterState): AccessVerdict =>
    collectAncestors(state).reduce<AccessVerdict>((acc, ancestor) => {
        if (acc.kind === "drop") {
            return acc;
        }

        const verdict = verdictForState(ancestor);

        if (verdict.kind === "drop") {
            return verdict;
        }

        return acc.kind === "lock" ? acc : verdict;
    }, OPEN);

const sidebarVerdictFor = (
    uiRouterState: string,
    sidebarEntry: { hidden: boolean; locked: boolean } | undefined,
    fallbackConfigId: string | null,
): AccessVerdict => {
    if (!sidebarEntry) {
        return OPEN;
    }

    if (sidebarEntry.hidden) {
        return DROP;
    }

    if (sidebarEntry.locked) {
        return lock(fallbackConfigId ?? uiRouterState);
    }

    return OPEN;
};

export const evaluateStateAccess = (
    uiRouterState: string | undefined,
    sidebarAccessMap: SidebarAccessMap,
): AccessVerdict => {
    if (!uiRouterState) {
        return OPEN;
    }

    const sidebarEntry = sidebarAccessMap.get(uiRouterState);

    if (sidebarEntry?.hidden) {
        return DROP;
    }

    const state = safeGetState(uiRouterState);

    if (!state) {
        return sidebarEntry?.locked ? lock(uiRouterState) : OPEN;
    }

    if (isRouterStateHidden(state)) {
        return DROP;
    }

    const ownConfigId = safeResolveConfigId(state);

    const ownVerdict = verdictForConfigId(ownConfigId);

    if (ownVerdict.kind === "drop") {
        return ownVerdict;
    }

    const parentVerdict = verdictFromAncestors(state);

    if (parentVerdict.kind === "drop") {
        return parentVerdict;
    }

    const sidebarVerdict = sidebarVerdictFor(uiRouterState, sidebarEntry, ownConfigId);

    return [ownVerdict, parentVerdict, sidebarVerdict].find(isLockVerdict) ?? OPEN;
};
